import { client } from '@/lib/apollo-client';
import {
  MEDIASILO_REVIEW_VIDEOS,
  type MediasiloReviewVideosQueryVariables,
} from '@/queries/mediasilo';
import type { VideoMetadata } from '@/lib/video-metadata';
import { isShareVideos } from '@/lib/frameio/shareVideos';
import { getMediasiloErrorCode } from './errors';
import { parseMediasiloUrl } from './links';

// The result is `unknown` so payloads are always validated at runtime.
type QueryFn = (options: {
  query: typeof MEDIASILO_REVIEW_VIDEOS;
  variables: MediasiloReviewVideosQueryVariables;
  fetchPolicy: 'no-cache';
  errorPolicy: 'all';
}) => Promise<{ data?: { mediasiloReviewVideos?: unknown } | null; error?: unknown; errors?: readonly unknown[] }>;

export async function getMediasiloReviewVideos(
  url: string,
  { query = client.query.bind(client) as unknown as QueryFn }: { query?: QueryFn } = {},
): Promise<VideoMetadata> {
  if (parseMediasiloUrl(url).kind !== 'review') throw new Error('MEDIASILO_UNSUPPORTED_LINK');
  let result;
  try {
    result = await query({
      query: MEDIASILO_REVIEW_VIDEOS, variables: { url }, fetchPolicy: 'no-cache', errorPolicy: 'all',
    });
  } catch (error) {
    throw new Error(getMediasiloErrorCode(error) ?? 'MEDIASILO_UNAVAILABLE');
  }
  if (result.error || result.errors?.length) throw new Error(getMediasiloErrorCode(result) ?? 'MEDIASILO_UNAVAILABLE');
  const review = result.data?.mediasiloReviewVideos;
  if (!isShareVideos(review)) throw new Error('MEDIASILO_UNAVAILABLE');
  return {
    platform: 'mediasilo',
    url,
    title: review.share_name ?? '',
    duration: Math.max(...review.videos.map((video) => video.duration_seconds)),
    live_status: 'not_live',
    shareVideos: review,
  };
}
